import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  Injector
} from '@angular/core';
import {ActivityShapeComponent} from '../../shapes/activity-shape/activity-shape.component';
import {ConnectorShapeComponent} from '../../shapes/connector-shape/connector-shape.component';
import {isComponent} from '../../helpers/shapes-detectors.util';
import {ContextBarComponent, ContextBarOptionGroup} from '@q9elements/ui-kit/components';

@Injectable()
export class AngularComponentService {
  private compMap = {};
  private compArr = [];

  constructor(
    private readonly cfr: ComponentFactoryResolver,
    private readonly aRef: ApplicationRef,
    private readonly injector: Injector
  ) {}

  public initActivityComponent(cell, data?) {
    const actComp = this.injectComponentIntoCell<ActivityShapeComponent>(
      cell,
      ActivityShapeComponent
    );

    actComp.instance.mxKey = Number(cell.id);

    if (data) {
      actComp.instance.setupFromJson(data);
    }

    return actComp;
  }

  public initConnectorComponent(cell, flMxKey) {
    const connectorComp = this.injectComponentIntoCell<ConnectorShapeComponent>(
      cell,
      ConnectorShapeComponent
    );

    connectorComp.instance.mxKey = Number(flMxKey);

    return connectorComp;
  }

  public initContextBarComponent(
    options: ContextBarOptionGroup[]
  ): ComponentRef<ContextBarComponent> | null {
    if (!options?.length) {
      return null;
    }

    const contextBarComp = this.cfr
      .resolveComponentFactory(ContextBarComponent)
      .create(this.injector);

    contextBarComp.instance.optionGroups = options;
    contextBarComp.changeDetectorRef.detectChanges();

    this.aRef.attachView(contextBarComp.hostView);

    return contextBarComp;
  }

  private injectComponentIntoCell<T>(cell, comp) {
    const cached = this.getCache(cell.id);
    const compInstance: {[key: string]: any; instance: T} =
      cached || (this.cfr.resolveComponentFactory(comp).create(this.injector) as any);
    const domNode = compInstance.location.nativeElement;

    cell.comp = compInstance;
    cell.div = domNode;
    this.setCache(cell.id, compInstance);

    return compInstance;
  }

  public refreshLabelStyles(cell) {
    if (isComponent(cell)) {
      const target = cell.div;

      cell.comp.instance.refreshParentStyle(target);
    }
  }

  public destroyComponent(cell) {
    if (isComponent(cell)) {
      this.removeCache(cell.id);
      cell.comp.destroy();
    }
  }

  public runDetection() {
    this.compArr.forEach(c => {
      if (!c.hostView.destroyed) {
        c.changeDetectorRef.detectChanges();
      }
    });
  }

  public clearCache() {
    this.compArr.forEach(c => c.destroy());
    this.compArr = [];
    this.compMap = {};
  }

  public getCache(id: number) {
    return this.compMap[id];
  }

  public setCache(id: number | string, comp) {
    if (!id || !comp) {
      return;
    }

    if (!this.compMap[id]) {
      this.compMap[id] = comp;
      this.compArr.push(comp);
    }
  }

  public removeCache(id: number) {
    const remIndex = this.compArr.indexOf(this.compMap[id]);

    this.compArr.splice(remIndex, 1);
    delete this.compMap[id];
  }
}
